import Tooltip from '../../components/tooltip'
import permissions from '../../components/permissions'
import {dialog as templateDialog} from '../../organizations/edit-template-form'

import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import excelExport from 'scoreboard/components/export/excel-export'
import sendToExcelTooltip from 'scoreboard/components/export/send-to-excel-tooltip'
import ReportConstants from 'scoreboard/reports/report-constants'
const { INDENTATION_PIXELS } = ReportConstants
import template from './rollup-tree-group-actions-table.hbs'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import addHrefToPrincipals from '../../components/add-href-to-principals'

/**
 * A table that is shown in a dialog with the user clicks the "View All" button
 * for a Rollup Tree Group.
 *
 * It shows all the tree nodes that have permissions and their ancestors.
 */
const RollupTreeGroupActionsTable = Backbone.View.extend({

	className: 'rollup-tree-group-actions-table',

	events: {
		'click .export-button': '_onClickExport',
		'click .edit-org-template': '_onClickEditOrgTemplate'
	},

	/**
	 * @param {Object} options
	 * @param {Boolean} [options.banner=false] indicates that a banner
	 * should be displayed that contains a link to the admin section
	 * @param {Number} [options.rollupTreeGroupId] ID of the rollup tree group.
	 * Required when the banner is displayed
	 * @param {Number} [options.treeRootId] ID of the root rollup tree item of
	 * the rollup tree group. Required when the banner is displayed
	 * @param {String} options.rollupTreeGroupLabel label of the rollup tree group
	 */
	initialize(options = {}) {
		this.options = options
		this.banner = options.banner ?? false
	},

	render: function () {
		const {rollupTreeGroupLabel, data = {}} = this.options
		const {tree, groups, users} = data
		this._nodesById = _.keyBy(tree, 'id')
		const labels = {
			user: _.keyBy(users, 'id'),
			group: _.keyBy(groups, 'id')
		}
		let previousDepth, previousNode
		_.each(tree, node => {
			const {depth, principals = []} = node
			node.hasPermissions = !_.isEmpty(principals)
			node.indent = depth * INDENTATION_PIXELS

			if (depth > previousDepth) { // child of previous
				node.parent = previousNode
			} else if (depth === previousDepth) { // sibling of previous
				node.parent = previousNode.parent
			}
			if (node.hasPermissions) {
				this._showAncestors(node)
			}
			previousDepth = depth
			previousNode = node
			_.each(principals, p => p.label = labels[p.type][p.id].label)
			addHrefToPrincipals(principals)
		})

		// if there aren't any tree items which should be shown, pass in 'isEmpty'
		// which will show an empty state message
		let isEmpty = true
		_.each(tree, t => {
			if (t.show) {
				isEmpty = false
			}
		})

		this.$el.empty().append(template({
			rollupTreeGroupLabel,
			tree,
			isEmpty,
			bannerHtml: this.bannerHtml()
		}))

		return this
	},

	/**
	 * Returns the HTML of a banner to display with a link to edit the rollup
	 * tree, if appropriate
	 *
	 * @return {String|null} HTML of the banner or null if no banner should be
	 * displayed
	 */
	bannerHtml: function() {
		if (!this.banner) return null

		let {treeRootId, rollupTreeGroupId} = this.options

		// create the "Dataset Rollup Tree" link
		let drtHref = `/admin/dataset-trees/${treeRootId}/rollup-tree-groups/${rollupTreeGroupId}`
		let drtLinkLabel = i18n.get('datasets.rollupTreeGroup.banner.datasetRollupTrees')
		let drtLink = `<a` +
			` class="text-link sp-navigate"` +
			` href="${drtHref}">` + _.escape(drtLinkLabel) + `</a>`
		let html = i18n.get('datasets.rollupTreeGroup.banner.1', drtLink)

		// create the "edit the organization template"
		if (this.options.organizationId &&
			this.options.organizationId != 'undefined') {
			let editLinkLabel = i18n.get('datasets.rollupTreeGroup.banner.editOrgTemplate')
			let editLink = `<a class="edit-org-template text-link">` +
				_.escape(editLinkLabel) +
				`</a>`
			html += ' ' + i18n.get('datasets.rollupTreeGroup.banner.2', editLink)
		}

		return html
	},

	/**
	 * If a descendant doesn't have permissions we must hide it, and any of
	 * it's ancestors that also have no permissions.
	 *
	 * https://github.com/SpiderStrategies/Scoreboard/issues/29610#issuecomment-758428929
	 */
	_showAncestors: function (node) {
		if (_.isNil(node) || node.show /* short-circuit */) {
			return
		}
		// Mark all the ancestors as shown, doing this one last so future
		// calls can short-circuit
		this._showAncestors(node.parent)
		node.show = true
	},

	_onClickExport: function (e) {
		this.sendToTooltip = sendToExcelTooltip($(e.currentTarget),
			this.$('h1').text(),
			{
				excelFn: this.exportTable.bind(this),
				replaceDialog: false
			})
	},

	_onClickEditOrgTemplate: function (e) {
		templateDialog(this.options.organizationId, { scrollToPermissions: true})
	},

	exportTable: function () {
		var opts = {
			label: this.options.rollupTreeGroupLabel,
			dataAttributes: {
				tr: 'data-id',
				modelLookup: this._modelLookup.bind(this)
			}
		}
		return excelExport.saveTableAsFile(this.$('tbody'), opts)
	},

	_modelLookup(tableId, tableRowIndex, jqCell, modelRowId) {
		if (!modelRowId) return // headers don't have ids
		const colIndex = jqCell.index()
		const {label = '', depth, principals} = this._nodesById[modelRowId]
		let value
		if (colIndex === 0) {
			const padding = _.padStart('', 4 * depth, ' ')
			value = padding + label
		} else if (colIndex ===1) {
			value = _.trim(_.join(_.map(principals, 'label'), '\n'))
		}
		const cellModel = {value}
		return cellModel
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.sendToTooltip], 'remove')
	}

})

/**
 * Opens RollupTreeGroupActionsTable in a dialog.
 *
 * @param {Number} rollupTreeGroupId ID of the rollup tree group
 * @param {String} rollupTreeGroupLabel label of the rollup tree group
 * @param {Number} treeRootId ID of the root rollup tree item of the rollup
 * tree group
 * @param {Boolean} [banner=false] indicates that a banner should be displayed
 * that contains a link to the admin section
 * @param {jQuery} errorTooltipTarget jQuery element that the error tooltip
 * should be attached to
 * @param {Number} organizationId the ID of the organization
 */
function dialog ({
	 	rollupTreeGroupId,
		rollupTreeGroupLabel,
		treeRootId,
		banner,
		errorTooltipTarget,
		organizationId
	}) {
	if (permissions.appAdmin()) {
		const url = `api/dataset-trees/${treeRootId}/rollup-tree-groups/${rollupTreeGroupId}/principals`
		appDisplay.dialog($.get(url), data =>
			new RollupTreeGroupActionsTable({
				rollupTreeGroupId,
				rollupTreeGroupLabel,
				treeRootId,
				data,
				banner,
				organizationId
			})
		)
	} else {
		this.permissionsTooltip = new Tooltip({
			attachTo: errorTooltipTarget,
			className: 'tooltip has-error',
			placement: 'above',
			arrow: 'center',
			content: i18n.get(
				'datasets.rollupTreeGroup.membersTable.noPermissionError'
			),
			closeOnAttachedToClick: false
		}).render()
	}
}

RollupTreeGroupActionsTable.dialog = dialog

export default RollupTreeGroupActionsTable
RollupTreeGroupActionsTable.__filename__ = 'admin/datasets/rollup-tree-group-actions-table.js'
